<template>
  <div id="app">
    <nav class="navbar" :class="{ 'navbar-scrolled': scrolled }">
      <div class="nav-container">
        <div class="nav-brand">
          <span class="brand-icon">M</span>
          <span class="brand-text">MookNote</span>
        </div>
        <ul class="nav-menu" :class="{ active: menuOpen }">
          <li><router-link to="/" exact @click.native="closeMenu">首页</router-link></li>
          <li><router-link to="/features" @click.native="closeMenu">功能特性</router-link></li>
          <li><router-link to="/guide" @click.native="closeMenu">使用指南</router-link></li>
          <li><router-link to="/contact" @click.native="closeMenu">联系我</router-link></li>
        </ul>
        <div class="nav-toggle" :class="{ active: menuOpen }" @click="toggleMenu">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </nav>
    <main class="main-content">
      <router-view/>
    </main>
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-brand">
          <span class="brand-icon">M</span>
          <span>MookNote</span>
        </div>
        <p class="footer-desc">记录你的观影、阅读与笔记</p>
        <div class="footer-links">
          <router-link to="/">首页</router-link>
          <router-link to="/features">功能</router-link>
          <router-link to="/guide">指南</router-link>
          <router-link to="/contact">联系</router-link>
        </div>
        <div class="footer-links">
          <a href="https://www.iletter.top/" target="_blank">作者简介</a>
          <a href="https://blog.iletter.top" target="_blank">作者博客</a>
        </div>
        <p class="copyright">© 2025 MookNote. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>
<script>
export default {
  name: 'App',
  data() {
    return {
      menuOpen: false,
      scrolled: false
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen
      document.querySelector('.nav-menu').classList.toggle('active')
    },
    closeMenu() {
      this.menuOpen = false
      document.querySelector('.nav-menu').classList.remove('active')
    },
    handleScroll() {
      this.scrolled = window.scrollY > 10
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  --color-bg: #FFFFFF;
  --color-bg-secondary: #F5F5F5;
  --color-text: #1A1A1A;
  --color-text-secondary: #666666;
  --color-text-muted: #999999;
  --color-border: #E5E5E5;
  --color-accent: #1A1A1A;
  --font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

body {
  font-family: var(--font-family);
  color: var(--color-text);
  background-color: var(--color-bg);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Navbar */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-border);
  z-index: 1000;
  transition: box-shadow 0.3s ease, background 0.3s ease;
}

.navbar-scrolled {
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.98);
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 600;
  transition: transform 0.2s ease;
}

.nav-brand:hover {
  transform: scale(1.02);
}

.brand-icon {
  width: 32px;
  height: 32px;
  background: var(--color-accent);
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  transition: background 0.2s ease;
}

.nav-menu {
  display: flex;
  list-style: none;
  gap: 40px;
}

.nav-menu a {
  text-decoration: none;
  color: var(--color-text-secondary);
  font-size: 15px;
  font-weight: 500;
  transition: color 0.2s;
  position: relative;
}

.nav-menu a:hover,
.nav-menu a.router-link-active {
  color: var(--color-text);
}

.nav-menu a.router-link-active::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--color-accent);
  border-radius: 1px;
}

.nav-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
  padding: 5px;
}

.nav-toggle span {
  width: 24px;
  height: 2px;
  background: var(--color-text);
  transition: all 0.3s ease;
  border-radius: 1px;
}

.nav-toggle.active span:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.nav-toggle.active span:nth-child(2) {
  opacity: 0;
}

.nav-toggle.active span:nth-child(3) {
  transform: rotate(-45deg) translate(5px, -5px);
}

/* Main Content */
.main-content {
  flex: 1;
  padding-top: 64px;
}

/* Page transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Footer */
.footer {
  background: var(--color-bg-secondary);
  border-top: 1px solid var(--color-border);
  padding: 60px 24px 40px;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.footer-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
}

.footer-brand .brand-icon {
  transition: transform 0.3s ease;
}

.footer-brand:hover .brand-icon {
  transform: rotate(5deg);
}

.footer-desc {
  color: var(--color-text-secondary);
  font-size: 14px;
  margin-bottom: 24px;
}

.footer-links {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-bottom: 16px;
}

.footer-links a {
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;
}

.footer-links a:hover {
  color: var(--color-text);
}

.copyright {
  color: var(--color-text-muted);
  font-size: 13px;
  margin-top: 24px;
}

/* Responsive */
@media (max-width: 768px) {
  .nav-menu {
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    background: white;
    flex-direction: column;
    padding: 20px 24px;
    gap: 20px;
    border-bottom: 1px solid var(--color-border);
    transform: translateY(-100%);
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }

  .nav-menu.active {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
  }

  .nav-toggle {
    display: flex;
  }

  .footer-links {
    flex-wrap: wrap;
    gap: 20px;
  }
}
</style>
